import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Delete, Add, BugReport } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';

interface CustomPattern {
  id: string;
  name: string;
  pattern: string;
  category: string;
  confidence_threshold: number;
  action: string;
  match_count: number;
  false_positive_count: number;
  effectiveness: string;
  created_at: string;
}

interface PatternFormData {
  name: string;
  pattern: string;
  category: string;
  confidence_threshold: number;
  action: string;
}

const CustomPHIPatterns: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testPattern, setTestPattern] = useState('');
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset } = useForm<PatternFormData>({
    defaultValues: {
      name: '',
      pattern: '',
      category: 'other_unique_identifier',
      confidence_threshold: 0.8,
      action: 'alert'
    }
  });

  const { data: patterns } = useQuery({
    queryKey: ['customPHIPatterns'],
    queryFn: async () => {
      const response = await axios.get('/api/organizations/compliance/phi-patterns/metrics');
      return response.data.data;
    }
  });

  const addPatternMutation = useMutation({
    mutationFn: async (data: PatternFormData) => {
      const response = await axios.post('/api/organizations/compliance/phi-pattern', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customPHIPatterns'] });
      toast.success('Pattern added successfully');
      setOpen(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add pattern');
    }
  });

  const removePatternMutation = useMutation({
    mutationFn: async (patternId: string) => {
      const response = await axios.post('/api/organizations/compliance/phi-pattern/remove', {
        pattern_id: patternId
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customPHIPatterns'] });
      toast.success('Pattern removed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove pattern');
    }
  });

  const testPatternMutation = useMutation({
    mutationFn: async (data: { pattern: string; test_text: string }) => {
      const response = await axios.post('/api/organizations/compliance/phi-pattern/test', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      setTestResult(data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to test pattern');
    }
  });

  const onSubmit = (data: PatternFormData) => {
    addPatternMutation.mutate(data);
  };

  const handleTest = () => {
    testPatternMutation.mutate({ pattern: testPattern, test_text: testText });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Custom PHI Patterns</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
          Add Pattern
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Define custom regex patterns to detect organization-specific PHI identifiers
          </Typography>

          {patterns && patterns.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell align="right">Matches</TableCell>
                    <TableCell align="right">Effectiveness</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patterns.map((pattern: CustomPattern) => (
                    <TableRow key={pattern.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {pattern.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pattern.pattern}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={pattern.category} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={pattern.action}
                          size="small"
                          color={
                            pattern.action === 'block'
                              ? 'error'
                              : pattern.action === 'redact'
                                ? 'warning'
                                : 'info'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{pattern.match_count}</TableCell>
                      <TableCell align="right">{pattern.effectiveness}%</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => removePatternMutation.mutate(pattern.id)}
                          disabled={removePatternMutation.isPending}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No custom patterns defined. Click "Add Pattern" to create one.
            </Alert>
          )}

          <Box mt={2}>
            <Button variant="outlined" startIcon={<BugReport />} onClick={() => setTestOpen(true)}>
              Test Pattern
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Add Pattern Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Custom PHI Pattern</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Pattern Name"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="pattern"
                  control={control}
                  rules={{ required: 'Pattern is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Regex Pattern"
                      multiline
                      rows={2}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'JavaScript regex pattern'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>PHI Category</InputLabel>
                      <Select {...field} label="PHI Category">
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="phone_number">Phone Number</MenuItem>
                        <MenuItem value="ssn">SSN</MenuItem>
                        <MenuItem value="medical_record_number">Medical Record Number</MenuItem>
                        <MenuItem value="account_number">Account Number</MenuItem>
                        <MenuItem value="other_unique_identifier">Other Identifier</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="confidence_threshold"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Confidence Threshold"
                      type="number"
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="action"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Action</InputLabel>
                      <Select {...field} label="Action">
                        <MenuItem value="alert">Alert</MenuItem>
                        <MenuItem value="redact">Redact</MenuItem>
                        <MenuItem value="block">Block</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={addPatternMutation.isPending}>
              {addPatternMutation.isPending ? 'Adding...' : 'Add Pattern'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Test Pattern Dialog */}
      <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Test PHI Pattern</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Regex Pattern"
                value={testPattern}
                onChange={(e) => setTestPattern(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Test Text"
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            {testResult && (
              <Grid item xs={12}>
                <Alert severity={testResult.valid ? 'success' : 'error'}>
                  {testResult.valid ? (
                    <>
                      <Typography variant="body2">
                        Pattern is valid! Found {testResult.match_count} match(es).
                      </Typography>
                      {testResult.matches.map((match: any, idx: number) => (
                        <Typography key={idx} variant="caption" display="block">
                          Match: "{match.text}" at position {match.position.start}
                        </Typography>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2">Invalid pattern: {testResult.error}</Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleTest}
            disabled={!testPattern || !testText || testPatternMutation.isPending}
          >
            Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomPHIPatterns;
